/**
 * Compact product card used inside recommendation strips ("You may also like"
 * on the product detail page; "Recommended for you" in the cart modal).
 *
 * Visual design matches the shop product grid cards so strips feel consistent
 * across the biz homepage, shop, and cart modal.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiPlus } from 'react-icons/fi';
import ColorPickerModal from '../../Pages/ProductDetail/ColorPickerModal';
import ProductCarousel from '../ProductCarousel';
import { productCardImages, needsVariantPicker } from '../../utils/productImages';
import { cheapestCampaignPrice, effectiveVariantPrice } from '../../utils/shopPromotions';
import OrderOfferNote from '../OrderOfferNote';
import { shopPrice } from '../../utils/currency';
import { useCurrency } from '../../Context/CurrencyContext';
import { getBasePath } from '../../utils/domainMode';
import { productPath } from '../../utils/productUrl';
import { isAskProduct, hidesItsPrice } from '../../utils/orderMode';
import { journeyGallery } from '../../utils/shopJourney';
import { localizedProduct, scriptOf } from '../../utils/shopperText';
import styles from './styles.module.css';

const RecMiniCard = ({
  product: shelfProduct, salonSlug, onAddToCart, onNavigate, autoSlide = false, peekHint = false,
  // The shop's campaigns, as `salonInfo.campaigns` carries them. Every place
  // this card is drawn passes them (asserted by
  // `Pages/Shop/__tests__/everyCardIsHandedTheCampaigns.test.js`): a card
  // handed none shows the stored price while the checkout charges the sale.
  campaigns,
}) => {
  const { t, i18n } = useTranslation();
  const currency = useCurrency();
  // The card in the reader's language (utils/shopperText); its address stays the shop's own. `onAddToCart` is handed
  // the same localized product, so the line it builds names it in the same words.
  const product = localizedProduct(shelfProduct, i18n?.language);
  const variants = product?.variants ?? [];
  // Preview the cheapest variant (with a "from" hint when prices differ). The +
  // opens the option picker for multi-variant products, so showing the lowest
  // price never undercuts what the customer is actually charged.
  // Price resolution handles the "null variant price → inherit mainVariant
  // defaultPrice" rule, so recommendation cards never show ₪0 by mistake —
  // and counts the campaigns through the one display rule the grid uses.
  const {
    variant: primary, price: cardPrice, compareAt, discounted, pct: discountPct, priceVaries,
  } = cheapestCampaignPrice(product, campaigns);
  /* The variations' photographs, then the PRODUCT's own when they have none
   * — see `productCardImages`. This card is the "you may also like" strip,
   * which was ten cardboard boxes on all 529 dk-store products because it
   * read the variation table alone and that table is empty for a
   * WooCommerce catalogue. */
  const images = productCardImages(product, variants);
  const showImages = images.map((img, i) => ({ id: img.id || img.url || String(i), url: img.url }));
  const picker = needsVariantPicker(product);
  const stockTracked = !!product?.trackInventory;
  const totalStock = stockTracked
    ? variants.reduce((acc, v) => acc + Number(v.stockOnHand ?? 0), 0)
    : Infinity;
  // An ask product is ordered by asking: never sold out from a shelf, never quick-added (utils/orderMode).
  const asks = isAskProduct(product);
  const isOut = !asks && totalStock <= 0;

  const price = cardPrice != null ? cardPrice : null;
  // `discounted` comes from `discountPercent` inside the rule — the shared
  // door — so this card cannot drift back into offering "−100%" the way it did
  // for the ₪0 bracelet.

  /* The quick-add sheet starts with nothing chosen (panel S2): the option this card leads with is its photo and its
   * price, not the shopper's choice. */
  const [pickedColor, setPickedColor] = useState(null);
  const [pickedSize, setPickedSize] = useState(null);
  const [pickedStyle, setPickedStyle] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [justAdded, setJustAdded] = useState(false);

  const flashAdded = () => {
    setJustAdded(true);
    setTimeout(() => setJustAdded(false), 600);
  };

  /* WHAT ONE UNIT OF THE ADDED OPTION IS SHOWN AT — handed to `onAddToCart` as its third argument
   * for the pixel's AddToCart (Context/CartContext): the price this card printed for the option it
   * leads with, and the same display rule for an option picked in the sheet. */
  const shownPriceOf = (variant) => (variant === primary && price != null
    ? price
    : effectiveVariantPrice(variant, product, campaigns).price);

  const handlePlus = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isOut) return;
    if (picker) {
      setModalOpen(true);
      return;
    }
    if (primary) {
      onAddToCart?.(product, primary, shownPriceOf(primary));
      flashAdded();
    }
  };

  const handleModalAdd = (variant) => {
    const matching = variant ?? variants.find((v) =>
      (!pickedColor || v.color === pickedColor)
      && (!pickedSize || v.size === pickedSize)
      && (!pickedStyle || v.style === pickedStyle),
    ) ?? variants[0];
    if (matching?.id) {
      onAddToCart?.(product, matching, shownPriceOf(matching));
      flashAdded();
    }
  };

  return (
    <article
      className={`${styles.card} ${justAdded ? styles.cardAdded : ''}`}
      data-testid={`rec-mini-card-${product.id}`}
    >
      <Link to={productPath(getBasePath(salonSlug), product)} className={styles.link} onClick={onNavigate}>
        <div className={styles.imgWrap}>
          <ProductCarousel
            images={showImages}
            autoSlide={autoSlide}
            peekHint={peekHint}
            alt={product.name}
            // A 148px mini card: the card-sized rendition.
            sizes="148px"
            onShopperMove={(photoIndex, action) => journeyGallery(`card:${product.id}`, { action, photoIndex, photoCount: showImages.length, productId: product.id })}
          />
          {discounted && (
            <span className={styles.discountBadge}>
              <span dir="ltr">-{discountPct}%</span>
            </span>
          )}
          {!isOut && !asks && (
            <button
              type="button"
              className={styles.plus}
              onClick={handlePlus}
              aria-label={t('rec.quickAdd', { defaultValue: 'Quick add' })}
              data-testid={`rec-mini-card-add-${product.id}`}
            >
              <FiPlus />
            </button>
          )}
        </div>
        <div className={styles.body}>
          <h3 className={styles.name} data-script={scriptOf(product.name)}>{product.name}</h3>
          {hidesItsPrice(product) && (
            <div className={styles.priceRow}>
              <span className={styles.askPrice}>{t('productDetail.askForPrice', { defaultValue: 'Ask the shop for the price' })}</span>
            </div>
          )}
          {price != null && !hidesItsPrice(product) && (
            <div className={styles.priceRow}>
              {priceVaries && <span className={styles.fromLabel}>{t('shop.priceFrom', { defaultValue: 'from' })}</span>}
              <span className={styles.price}>{shopPrice(price, currency)}</span>
              {discounted && <span className={styles.compareAt}>{shopPrice(compareAt, currency)}</span>}
            </div>
          )}
          {/* A once-per-order amount is not in the card's price; it is named here (UX-FINAL-11). */}
          <OrderOfferNote product={product} variant={primary} campaigns={campaigns} currency={currency} className={styles.fromLabel} />
        </div>
      </Link>

      {modalOpen && (
        <ColorPickerModal
          open
          product={product}
          pickedColor={pickedColor}
          setPickedColor={setPickedColor}
          pickedSize={pickedSize}
          setPickedSize={setPickedSize}
          pickedStyle={pickedStyle}
          setPickedStyle={setPickedStyle}
          onClose={() => setModalOpen(false)}
          onAddToCart={handleModalAdd}
          t={t}
        />
      )}
    </article>
  );
};

export default RecMiniCard;
